import { showNotification } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import NotificationSettingsForm, {
  NotificationSettingsFormData,
} from 'components/forms/notification-settings/NotificationSettingsForm';
import { HrTermDueDateQuery } from 'components/forms/notification-settings/HrNotificationCalendarPreview';
import useOrganizationModules from 'features/organization-modules/use-organization-modules';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { DASHBOARD_PAGE_PATH } from 'routes/paths';
import { ERROR_NOTIFICATION_COLOR, SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import { UserPreviewHrNotificationEmailPayload } from 'api/actions/user-preview-hr-notification-email/user-preview-hr-notification-email-payload';

/**
 * Page where the user can set their notifications.
 */
export default function NotificationSettingsPage() {
  const { fullName, getAction, userId } = useApi();
  const { hr, revisions } = useOrganizationModules();
  const { userId: paramsUserId } = useParams();
  const [displayedName, setDisplayedName] = useState('');

  const { data, loading, error } = useImmediateAction(() => {
    const userGetNotificationSettings = getAction('UserGetNotificationSettings');

    return userGetNotificationSettings({ parameters: { userId: paramsUserId! } });
  });

  const loadHrTermDueDates = useCallback(
    async ({ from, to, departmentIds, termTypeIds }: HrTermDueDateQuery) => {
      try {
        const terms = await getAction('HrListTerms')({
          query: { filters: { status: 'active', dueFrom: from, dueTo: to } },
        });
        const allowedDepartments = new Set(departmentIds);
        const allowedTermTypes = new Set(termTypeIds);
        const dueDates = new Set<string>();
        terms.forEach((term) => {
          if (
            term.nextDueDate &&
            term.departmentId !== undefined &&
            allowedDepartments.has(term.departmentId) &&
            term.termTypeId !== undefined &&
            allowedTermTypes.has(term.termTypeId)
          ) {
            dueDates.add(term.nextDueDate);
          }
        });
        return Array.from(dueDates);
      } catch (termLoadError) {
        panic(termLoadError instanceof Error ? termLoadError : new Error(String(termLoadError)));
        return [];
      }
    },
    [getAction]
  );

  const loadHrEmailPreview = useCallback(
    (payload: UserPreviewHrNotificationEmailPayload) =>
      getAction('UserPreviewHrNotificationEmail')({
        parameters: { userId: paramsUserId! },
        payload,
      }),
    [getAction, paramsUserId]
  );

  /**
   * Handles the form submission.
   */
  function submit({
    revisions,
    revisionPlans,
    departmentIds,
    deviceTypeIds,
    hrAvailable,
    hrTermNotifications,
    hrTermNotificationLeadDays,
    hrTermNotificationPeriod,
    hrTermNotificationTime,
    hrTermNotificationWeekday,
    hrTermNotificationMonthDay,
    hrTermNotificationWindowType,
    hrTermNotificationWindowValue,
    hrTermNotificationUpcomingRepetition,
    hrTermNotificationOverdueMode,
    hrDepartmentIds,
    hrTermTypeIds,
  }: NotificationSettingsFormData) {
    const action = getAction('UserUpdateNotificationSettings');

    return action({
      parameters: {
        userId: paramsUserId!,
      },
      payload: {
        revisions,
        revisionPlans,
        departmentIds: departmentIds ? departmentIds.map((id) => Number(id)) : [],
        deviceTypeIds: deviceTypeIds ? deviceTypeIds.map((id) => Number(id)) : [],
        ...(hrAvailable
          ? {
              hrTermNotifications,
              hrTermNotificationLeadDays,
              hrTermNotificationPeriod,
              hrTermNotificationTime,
              hrTermNotificationWeekday,
              hrTermNotificationMonthDay,
              hrTermNotificationWindowType,
              hrTermNotificationWindowValue,
              hrTermNotificationUpcomingRepetition,
              hrTermNotificationOverdueMode,
              hrDepartmentIds: hrDepartmentIds.map(Number),
              hrTermTypeIds: hrTermTypeIds.map(Number),
            }
          : {}),
      },
    })
      .then(() => {
        const message =
          userId === Number(paramsUserId!)
            ? 'Vaše upozornenia boli úspešne nastavené'
            : `Upozornenia pre ${displayedName} boli úspešne nastavené.`;

        showNotification({
          title: 'Upozornenia boli nastavené',
          message,
          color: SUCCESS_NOTIFICATION_COLOR,
        });
      })
      .catch((error) => {
        const status = error?.response?.status;
        if (status === 403 || status === 422) {
          showNotification({
            title: 'Nastavenia sa nepodarilo uložiť',
            message:
              status === 403
                ? 'Nemáte oprávnenie uložiť tieto nastavenia. Obnovte stránku a skúste to znova.'
                : 'Skontrolujte nastavenia upozornení a skúste to znova.',
            color: ERROR_NOTIFICATION_COLOR,
          });
        } else {
          panic(error);
        }
      });
  }

  useEffect(() => {
    if (paramsUserId && Number(paramsUserId) === userId) {
      setDisplayedName(fullName!);
    } else {
      const userGetAction = getAction('UserGet');

      userGetAction({ parameters: { userId: paramsUserId! } }).then((response) => {
        setDisplayedName(`${response.firstName} ${response.lastName}`);
      });
    }
  }, [userId, paramsUserId]);

  // Display the error if there is one.
  useEffect(() => {
    if (error) {
      panic(error);
    }
  }, [error]);

  if (!userId) {
    return <Navigate to={DASHBOARD_PAGE_PATH.original} />;
  }

  return (
    <DashboardLayout
      title="Nastavenia upozornení"
      breadcrumbs={[{ title: displayedName }, { title: 'Nastavenia upozornení' }]}
    >
      {!loading && !error && (
        <NotificationSettingsForm
          onSubmit={submit}
          revisionsAvailable={revisions}
          loadHrTermDueDates={loadHrTermDueDates}
          loadHrEmailPreview={loadHrEmailPreview}
          initialValues={{
            revisions: data.revisions,
            revisionPlans: data.revisionPlans,
            departmentIds: data.departmentIds.map((id) => String(id)),
            deviceTypeIds: data.deviceTypeIds.map((id) => String(id)),
            hrAvailable: hr && data.hrAvailable,
            hrTermNotifications: data.hrTermNotifications ?? false,
            hrTermNotificationLeadDays: data.hrTermNotificationLeadDays ?? 7,
            hrTermNotificationPeriod: data.hrTermNotificationPeriod ?? 'daily',
            hrTermNotificationTime: data.hrTermNotificationTime ?? '06:00',
            hrTermNotificationWeekday: data.hrTermNotificationWeekday ?? 1,
            hrTermNotificationMonthDay: data.hrTermNotificationMonthDay ?? 0,
            hrTermNotificationWindowType: data.hrTermNotificationWindowType ?? 'days',
            hrTermNotificationWindowValue: data.hrTermNotificationWindowValue ?? data.hrTermNotificationLeadDays ?? 7,
            hrTermNotificationUpcomingRepetition: data.hrTermNotificationUpcomingRepetition ?? 'once',
            hrTermNotificationOverdueMode: data.hrTermNotificationOverdueMode ?? 'once',
            hrDepartmentIds: (data.hrDepartmentIds ?? []).map(String),
            hrTermTypeIds: (data.hrTermTypeIds ?? []).map(String),
            hrAvailableDepartmentIds: data.hrAvailableDepartmentIds ?? [],
            hrAvailableTermTypeIds: data.hrAvailableTermTypeIds ?? [],
          }}
        />
      )}
    </DashboardLayout>
  );
}
